import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Injector, ViewChild } from '@angular/core';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { AlertController } from 'providers/alert-controller';
import { AuthService } from 'services/auth.service';
import { DebugService } from 'services/debug.service';
import { SettingsService } from 'services/settings.service';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { UserDb, TaskStatusCacheUpdatedEvent } from 'services/user-db.service';
import { taskStatusChangeAffectsVisibleDays, TaskStatusChangeRange } from 'services/user-db.task-status';
import { FoodDbService } from 'services/food-db.service';
import { PubSubRegistration, PubSubService } from 'services/pubsub.service';
import { LoggingService } from 'services/logging.service';
import { UiDisplayNutrient } from 'models/display-nutrient';
import { FormatNutrients, formatRelativeDay } from 'util/formatting';
import { CsDayItemsComponent } from 'components/cs-day-items/cs-day-items';
import { NutrientDisplayMode } from 'components/nutrient-list/nutrient-list';
import { Food, getIngredientText, calculateNutrientMatch_pc } from '@cf-shared/data-model/food';
import { MealTunerMeal } from '@cf-shared/meal-tuner-interface';
import { MealPhotoScannerResult } from 'pages/meal-photo-scanner-page/meal-photo-scanner-interface';
import { Log_DayStart } from '@cf-shared/data-model/log-day-start';
import { isRecipe } from '@cf-shared/data-model/recipe';
import { DateTime } from 'luxon';
import { TaskStatus } from 'components/cs-task-calendar-day/cs-task-calendar-day';
import { formatWeekdayDatePaddedWithYear } from '@cf-shared/date-format';
import { upperFirst } from 'lodash-es';
import { PopupMenuProvider } from 'providers/popup-menu/popup-menu';
import { IProgramSettingsPage, ProgramSettingsTab } from 'pages/program-settings-page/program-settings-interface';
import { getTransitionSlideInOutMs, waitForAngularUpdate } from 'services/browser-utils';
import { DayAnimationQueue } from 'services/day-animation-queue';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { ITaskCalendarPage } from 'pages/task-calendar-page/task-calendar-interface';
import { IMealPlanPage } from 'pages/meal-plan/meal-plan-interface';
import { IFoodsPage, FoodsPageResult } from 'pages/foods/foods-interface';
import { NutritionPageParams, NutritionTab } from './nutrition-interface';

const DAY_CONTENT_ANIMATION_MS = getTransitionSlideInOutMs();

// which of the two day copies the page is talking about. The page draws the day
// twice so it can cross-fade one out while the next slides in.
type DaySlotId = 'A' | 'B';

// everything one day copy holds. Two of these instead of a lettered pair of
// fields per value, so anything new the day card shows is one field here rather
// than two fields plus a two-branch write at every place that sets it.
interface NutritionDaySlot {
  displayNutrients: UiDisplayNutrient[];
  match_pc: number | undefined;
  loading: boolean;
}

// what one day's nutrients came out as, before it is stored in a slot. Kept
// separate from the slot so the caller decides WHEN to store it — the two
// callers differ on that, see loadDayNutrientsAsync.
type LoadedDayNutrients = Pick<NutritionDaySlot, 'displayNutrients' | 'match_pc'>;

// a day slot as it starts out: nothing loaded and not loading
function createEmptyDaySlot(): NutritionDaySlot {
  return { displayNutrients: undefined, match_pc: undefined, loading: false };
}

@Component({
    selector: 'page-nutrition',
    templateUrl: 'nutrition.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NutritionPage extends CsPage<NutritionPageParams> {
  protected LL = LL;
  @ViewChild('dayItemsA') dayItemsA: CsDayItemsComponent;
  @ViewChild('dayItemsB') dayItemsB: CsDayItemsComponent;

  recentFoodsText: string = '';

  protected day: Log_DayStart;

  // the two day copies, one record each. The template reads them by letter
  // because it draws both slots; the code below reaches them through slot().
  protected slots: Record<DaySlotId, NutritionDaySlot> = {
    A: createEmptyDaySlot(),
    B: createEmptyDaySlot(),
  };

  protected animateDayContent = false;
  protected activeDaySlot: DaySlotId = 'A';
  protected leavingSlot: DaySlotId | null = null;
  protected enteringSlot: DaySlotId | null = null;
  protected isBack: boolean = false;

  // queue for rapid day changes - prevents animation interruption
  private dayQueue = new DayAnimationQueue();

  // getter for the slot the user is currently looking at
  protected get activeSlot(): NutritionDaySlot {
    return this.slots[this.activeDaySlot];
  }

  // the record belonging to one named slot
  private slot(slotId: DaySlotId): NutritionDaySlot {
    return this.slots[slotId];
  }

  // the day-items component belonging to a slot. The two components stay
  // separate fields because an Angular view query binds to a named field, so
  // this is the one place that still picks between them by letter.
  private dayItemsFor(slotId: DaySlotId): CsDayItemsComponent {
    return slotId === 'A' ? this.dayItemsA : this.dayItemsB;
  }

  // getter for active day items component
  get dayItemsComponent(): CsDayItemsComponent {
    return this.dayItemsFor(this.activeDaySlot);
  }

  // getter for active display nutrients
  get displayNutrientsDay(): UiDisplayNutrient[] {
    return this.activeSlot.displayNutrients;
  }

  // getter for active match_pc
  get match_pc(): number | undefined {
    return this.activeSlot.match_pc;
  }
  calendarTaskStatuses: Map<string, TaskStatus> = new Map();
  currentDay: DateTime;
  currentDayLabel: string;
  fakeToday: string;
  currentWeekday: string;

  hasClientSmallScreen = hasClientSmallScreen;
  selectedTab: NutritionTab = NutritionTab.Logged;
  NutritionTab = NutritionTab; // Make enum available in template
  NutrientDisplayMode = NutrientDisplayMode; // Make enum available in template

  private taskStatusCacheUpdatedReg: PubSubRegistration;

  constructor(
    public navCtrl: NavController,
    public alertCtrl: AlertController,
    protected auth: AuthService,
    protected settings: SettingsService,
    protected debug: DebugService,
    public cloudFunctionsService: CloudFunctionsService,
    public userDb: UserDb,
    private foodDb: FoodDbService,
    private cdr: ChangeDetectorRef,
    private popupMenu: PopupMenuProvider,
    private elementRef: ElementRef,
    private injector: Injector,
    private pubSub: PubSubService,
    private logging: LoggingService,
  ) {
    super();
  }

  async ionViewCanEnter() {
    let isoDateLocal = this.navParams.data.day;
    if (isoDateLocal) {
      this.currentDay = DateTime.fromISO(isoDateLocal).startOf('day');
    }
    if (!this.currentDay || !this.currentDay.isValid) {
      this.currentDay = this.debug.getNowLocal().startOf('day');
    }
  }

  async getRecentFoodsTexts() {
    // get all user's foods
    let userFoods: Food[] = await this.foodDb.getUserFoodsAsync();
    // filter only those that are used (a lot of recipe contents are cached but not directly used)
    let recentlyUsedFoods = userFoods.filter(f => f.local && f.local.lastUsed_utc);
    // sort by last used time
    recentlyUsedFoods = recentlyUsedFoods.sort((a, b) => b.local.lastUsed_utc.localeCompare(a.local.lastUsed_utc));

    // abbreviated text for the header
    const maxFoodsToList = 5;
    let shortList = recentlyUsedFoods.slice(0, maxFoodsToList);
    let shortlistText = getIngredientText(shortList);
    if (recentlyUsedFoods.length > shortList.length) {
      shortlistText += '...';
    }

    // count used recipes (includes legacy meals) and foods
    let usedRecipes = recentlyUsedFoods.filter(f => isRecipe(f)).length;
    let usedFoods = recentlyUsedFoods.filter(f => !isRecipe(f)).length;
    if (usedFoods > 0 || usedRecipes > 0) {
      this.recentFoodsText = `${usedRecipes} recipes, ${usedFoods} foods used so far.\n(${shortlistText})`;
    } else {
      this.recentFoodsText = 'No foods used yet. Use the buttons below to explore foods and recipes or create your own.';
    }

  }

  // reads one day's nutrients and returns what a slot should hold for it. It
  // deliberately does NOT store that into a slot: the two callers differ in what
  // they do between the read and the store — the day switch abandons the store
  // when the view went away mid-read, this one does not — so the store stays at
  // the call site where that difference is visible. It DOES write `this.day`,
  // the page-level field both load paths have always set, and it writes it
  // before the caller's destroyed-view check can abandon anything.
  private async loadDayNutrientsAsync(date: DateTime): Promise<LoadedDayNutrients> {
    ({ day: this.day } = await this.userDb.getDayMealsAsync(date));
    // if the day has nothing logged, the slot shows no nutrients at all
    if (!this.day) {
      return { displayNutrients: null, match_pc: undefined };
    }
    return {
      displayNutrients: FormatNutrients(this.day.nutrients, this.userDb, { targets: this.day.targets }),
      match_pc: calculateNutrientMatch_pc(this.day.nutrients, this.day.targets),
    };
  }

  // puts a loaded day's nutrients into one slot, so the template shows them there
  private storeDayNutrients(slotId: DaySlotId, loaded: LoadedDayNutrients) {
    const slot = this.slot(slotId);
    slot.displayNutrients = loaded.displayNutrients;
    slot.match_pc = loaded.match_pc;
  }

  async refreshData() {
    let now = this.debug.getNowLocal();
    // Use the currentDay if it's set, otherwise use today
    let targetDay = this.currentDay || now.startOf('day');
    await this.dayItemsComponent.loadScheduleAsync('meals', now, targetDay, undefined);

    const loaded = await this.loadDayNutrientsAsync(targetDay);

    // update active slot data. The active slot is read AFTER the load, not
    // before it: a day tap during the load moves it, and this has always stored
    // into whichever slot is active when the answer arrives.
    this.storeDayNutrients(this.activeDaySlot, loaded);

    this.safeMarkForCheck(this.cdr);
  }

  async ngOnInit() {

    // subscribe to task-status cache updates so the calendar day markers
    // refresh when the cache recomputes — most importantly the #4219 one-shot
    // that fires when the catch-up sync completes after a display-only boot load
    this.taskStatusCacheUpdatedReg = this.pubSub.subscribe(TaskStatusCacheUpdatedEvent, (range) => {
      if (this.viewDestroyed) return;
      void this.loadMealTaskStatuses(range).catch(err => this.logging.trackAbnormalException(err));
    });

    // this try block makes sure that we don't leave the spinner on forever
    try {
      // slot A by name, not by lookup: this runs before any day switch can have
      // happened, so the active slot is still the one the page starts on
      this.slots.A.loading = true;
      this.cdr.markForCheck();

      let now = this.debug.getNowLocal();
      let todayBOD = now.startOf('day');
      this.currentDay = todayBOD;

      // if not faking today
      if (now.hasSame(DateTime.local(), 'day')) {
        this.fakeToday = '';
        // if faking today
      } else {
        this.fakeToday =
          '[' +
          formatWeekdayDatePaddedWithYear(now, BOOT_LOCALE) +
          ']';
      }

      this.currentDayLabel = upperFirst(formatRelativeDay(now, this.currentDay));
      this.currentWeekday = this.currentDay.weekday.toFixed(0);

      // deliberately NOT awaited: the task-status load can block for a long
      // time behind the task-status cache mutex (worst case: a full view-index
      // rebuild over the whole log history holds it for minutes). The page
      // must enter without it — the calendar fills in when the statuses
      // arrive, instantly when the in-memory memo already has them.
      // detached from the lifecycle promise, so a rejection must be surfaced
      // here — nothing upstream catches it anymore
      void this.loadMealTaskStatuses().catch(err => this.logging.trackAbnormalException(err));

    } finally {
      if (!this.viewDestroyed) {
        this.slots.A.loading = false;
        // enable animations after initial load
        this.animateDayContent = true;
        this.cdr.markForCheck();
      }
    }
  }

  async loadMealTaskStatuses(changedRange?: TaskStatusChangeRange) {
    const today = this.debug.getNowLocal().startOf('day');
    const endDate = today.plus({ days: 7 });
    const startDate = endDate.minus({ months: 1 });

    // skip a reload for a change that is about days this calendar does not
    // show. During a backlog sync most events are: a burst backfilling old log
    // entries publishes an old range, and reloading for it would be pure churn
    // (#4249). No range given means "unknown" — reload, the safe direction.
    //
    // The check reads the whole event, not just its bounding span. The span
    // alone still suppresses plenty — a wave lying entirely in old history
    // fails the overlap test outright. What it cannot suppress is a wave whose
    // days STRADDLE this window with none inside it, and that is what the
    // clusters are for (#4451).
    if (changedRange && !taskStatusChangeAffectsVisibleDays(
      changedRange, startDate.toISODate(), endDate.toISODate())) {
      return;
    }

    const dayStatuses = await this.userDb.getCachedTaskStatusesAsync(startDate, endDate);

    // Extract only meal statuses
    this.calendarTaskStatuses = new Map<string, TaskStatus>();
    dayStatuses.forEach((statuses, date) => {
      this.calendarTaskStatuses.set(date, statuses.mealsStatus);
    });

    this.safeMarkForCheck(this.cdr);
  }

  async loadTaskStatusesForRange(startDate: DateTime, endDate: DateTime): Promise<Map<string, TaskStatus>> {
    const dayStatuses = await this.userDb.getCachedTaskStatusesAsync(startDate, endDate);

    // Extract only meal statuses
    const result = new Map<string, TaskStatus>();
    dayStatuses.forEach((statuses, date) => {
      result.set(date, statuses.mealsStatus);
    });
    return result;
  }

  async onCalendarDateSelected(selectedDateTime: DateTime) {
    // if animation is in progress, queue this date and return
    if (!this.dayQueue.tryStart(selectedDateTime)) {
      return;
    }

    // on the happy path the queue is completed by releaseDayQueueAfterSlide →
    // clearLeavingSlotState; on every other exit (throw OR viewDestroyed early-return)
    // the finally must complete it, or subsequent date clicks on a re-entered page
    // would be silently dropped (viewDestroyed flips back to false on ionViewWillEnter).
    let handedOffToAnimation = false;
    try {
      // determine next slot (alternate between A and B)
      const nextSlot: DaySlotId = this.activeDaySlot === 'A' ? 'B' : 'A';
      const nextDayItems = this.dayItemsFor(nextSlot);

      // Step 1: Set up entering/leaving classes with correct direction
      this.isBack = selectedDateTime < this.currentDay;
      this.enteringSlot = nextSlot;
      this.leavingSlot = this.activeDaySlot;
      this.cdr.markForCheck();

      // Step 2: Wait for Angular to apply classes (entering-back has transition: none)
      await waitForAngularUpdate(this.injector);
      if (this.viewDestroyed) {
        // same drop, same reason, same report as the Training page — see the note
        // there. A day the user picked is not silently discarded any more (#4411).
        this.logging.trackSilentProblem('NutritionDayPickDroppedWhileLeaving', { date: selectedDateTime.toISODate() });
        return;
      }

      // Step 3: Now swap active slot - remove entering class, add active
      this.enteringSlot = null;
      this.activeDaySlot = nextSlot;
      this.currentDay = selectedDateTime;

      // set loading on next slot
      this.slot(nextSlot).loading = true;
      this.safeMarkForCheck(this.cdr);

      // Update URL params
      await this.navCtrl.updateCurrentPageParamsAsync({ day: selectedDateTime.toISODate() });
      if (this.viewDestroyed) return;

      const now = this.debug.getNowLocal();
      this.currentDayLabel = upperFirst(formatRelativeDay(now, selectedDateTime));
      this.currentWeekday = selectedDateTime.weekday.toFixed(0);
      this.safeMarkForCheck(this.cdr);

      // load into the next (now active) slot
      await nextDayItems.loadScheduleAsync('meals', now, selectedDateTime, undefined);
      if (this.viewDestroyed) return;

      // fire-and-forget, same change as the Training page's day switch — see
      // design-docs/reactive-sync.md, "Neither day switch waits for the calendar
      // dots" (#4410)
      void this.loadMealTaskStatuses().catch(err => this.logging.trackAbnormalException(err));

      // load day nutrients data for next slot
      const loaded = await this.loadDayNutrientsAsync(selectedDateTime);
      if (this.viewDestroyed) return;

      this.storeDayNutrients(nextSlot, loaded);

      // clear loading on next slot
      this.slot(nextSlot).loading = false;
      this.safeMarkForCheck(this.cdr);

      // release the day-pick queue once the slide has had time to finish
      this.releaseDayQueueAfterSlide();
      handedOffToAnimation = true;
    } finally {
      if (!handedOffToAnimation) {
        this.dayQueue.complete();
      }
    }
  }

  handleSwipeForward() {
    void this.onCalendarDateSelected(this.currentDay.plus({ days: 1 }));
  }

  handleSwipeBack() {
    void this.onCalendarDateSelected(this.currentDay.minus({ days: 1 }));
  }

  private clearLeavingSlotState() {
    // always complete the queue first so a destroyed view doesn't leave _isAnimating
    // stuck and silently drop every subsequent date click on the re-entered page
    // (see DayAnimationQueue JSDoc invariant); UI mutations + re-dispatch are gated below.
    const queued = this.dayQueue.complete();
    if (this.viewDestroyed) return;

    this.leavingSlot = null;
    this.isBack = false;
    this.cdr.markForCheck();

    // if there was a queued date, start the new animation
    if (queued) {
      void this.onCalendarDateSelected(queued);
    }
  }

  // release the day-pick queue once the slide's duration has passed. The slide
  // itself, and clearing the leaving panel's classes, belong to
  // cs-tab-content-container on its own timer; this timer only decides when a
  // queued day pick may start. A fixed timer is deliberate: the page has no
  // transition of its own to listen to (df-bd8185673235).
  private releaseDayQueueAfterSlide() {
    // raw setTimeout, NOT safeTimeout: safeTimeout is cancelled on view destroy,
    // which would leave the dayQueue permanently _isAnimating=true on the
    // re-entered page (silently dropping clicks). clearLeavingSlotState handles
    // the destroyed-view case itself: it always completes the queue first, then
    // guards UI mutations on viewDestroyed.
    setTimeout(() => this.clearLeavingSlotState(), DAY_CONTENT_ANIMATION_MS);
  }

  async onTitleClick() {
    const result = await this.navCtrl.callAsync(ITaskCalendarPage, {
      selectedDateISO: this.currentDay.toISO(),
      local: {
        taskStatusLoader: (startDate, endDate) => this.loadTaskStatusesForRange(startDate, endDate),
      },
    });

    if (result?.selectedDate) {
      void this.onCalendarDateSelected(result.selectedDate);
    }
  }

  handleAutoScrollToElement() {
    // Auto-scroll functionality removed since nutrition settings card moved to separate page
  }

  async ionViewDidReEnter() {
    this.handleAutoScrollToElement();
  }

  async ionViewWillEnter() {
    // skip reload when returning from a modal - the caller awaits callAsync which handles date changes
    const pageElement = this.elementRef.nativeElement as HTMLElement;
    const wasUnderModal = pageElement.classList.contains('previous-visible');

    if (wasUnderModal) {
      return;
    }

    // set loading on active slot
    this.activeSlot.loading = true;
    this.cdr.markForCheck();

    let now = this.debug.getNowLocal();

    // if not faking today
    if (now.hasSame(DateTime.local(), 'day')) {
      this.fakeToday = '';
      // if faking today
    } else {
      this.fakeToday =
        '[' +
        formatWeekdayDatePaddedWithYear(now, BOOT_LOCALE) +
        ']';
    }

    this.currentDayLabel = upperFirst(formatRelativeDay(now, this.currentDay));
    this.currentWeekday = this.currentDay.weekday.toFixed(0);

    // this try block makes sure that a failed day load does not leave the spinner
    // on forever, the same way ngOnInit does. no catch: the error still reaches
    // cs-nav's page-hook reporting, and the auto-scroll below stays skipped.
    try {
      await this.refreshData();

      // not awaited on purpose — see ngOnInit: the day card is hidden while the
      // slot's loading flag is set, and this call waits on the task-status cache
      // mutex, which a view-index rebuild can hold for minutes. the card only
      // needs refreshData; the calendar dots fill in when the statuses arrive.
      // rejection surfaced here because the detached promise no longer reaches
      // cs-nav's lifecycle catch
      void this.loadMealTaskStatuses().catch(err => this.logging.trackAbnormalException(err));
    } finally {
      if (!this.viewDestroyed) {
        // clear loading on active slot
        this.activeSlot.loading = false;
        this.cdr.markForCheck();
      }
    }

    this.handleAutoScrollToElement();
  }

  async ionViewWillLeave() {
  }


  async handleSettingsButtonClicked(event: Event) {
    const targetElement = event.currentTarget as HTMLElement;

    await this.popupMenu.createMenu({
      targetFromElement: targetElement,
      buttons: [
        {
          text: LL.settings.items.nutritionSettings(),
          icon: 'settings',
          handler: () => {
            void this.navCtrl.pushAsync(IProgramSettingsPage, { tab: ProgramSettingsTab.Nutrition });
          }
        },
        {
          text: LL.settings.items.mealPlanner(),
          icon: 'assignment',
          handler: () => {
            this.openMealPlanner();
          }
        },
        {
          text: LL.settings.items.foods(),
          icon: 'grocery',
          handler: () => {
            void this.navCtrl.pushAsync(IFoodsPage, { mode: 'list' });
          }
        }
      ]
    });
  }

  // opens the Meal Planner. Shared by the settings menu row and the "Plan My
  // Week" button at the bottom of the day view (#4972), so the two doors into
  // the planner cannot drift apart.
  openMealPlanner() {
    void this.navCtrl.pushAsync(IMealPlanPage, {});
  }

  handleFoodsButtonClicked(filter: string) {
    void this.navCtrl.pushAsync(IFoodsPage, { mode: 'list', filter });
  }

  async onMealSuggestionResult(context: { mealId: string }, suggestion: MealTunerMeal | undefined) {
    await this.dayItemsComponent?.onMealSuggestionResult(context, suggestion);
  }

  async onMealPhotoScanResult(context: { mealId: string }, result: MealPhotoScannerResult) {
    await this.dayItemsComponent?.onMealPhotoScanResult(context, result);
  }

  // forwards the meal photo scanner's product-label redirect food pick to cs-day-items (#3902)
  async onMealScanRedirectFood(context: { mealId: string }, result: FoodsPageResult) {
    await this.dayItemsComponent?.onMealScanRedirectFood(context, result);
  }

  ngOnDestroy() {
    if (this.taskStatusCacheUpdatedReg) {
      this.taskStatusCacheUpdatedReg.unsubscribe();
      this.taskStatusCacheUpdatedReg = undefined;
    }
  }
}
